/* guide U1: 가로 스크롤 + 하단 커스텀 스크롤바 (ai-lists.js gp1TableScroll 포팅).
 *
 * 제품 비교 테이블(CompareTableCard)과 심플 제품 카드(SimpleProductCard)가
 * 동일한 가로 스와이프(휠→가로 변환 · grab-to-pan 드래그 · 커스텀 스크롤바 동기)를 공유한다.
 *
 * 마크업 전제 (호출 측 동일 구조):
 *   .ai-gp1-scroll--container(ref=container)
 *   .ai-gp1-custom--scrollbar(ref=scrollbar) > .ai-gp1-scrollbar--track(ref=track) > .ai-gp1-scrollbar--bar(ref=bar)
 */

import { useEffect } from "react";

/* 클릭과 드래그를 구분하는 이동 임계값(px). 이보다 적게 움직이면 클릭으로 처리. */
const _DRAG_THRESHOLD_PX = 5;
/* 드래그 중 컨테이너에 부여하는 클래스 (cursor: grabbing · 텍스트 선택 차단) */
const _GRABBING_CLASS = "is-grabbing";
/* 가로 스크롤 가능할 때 부여하는 클래스 (cursor: grab 어포던스) */
const _DRAGGABLE_CLASS = "is-draggable";

export function useDragScrollbar(
  containerRef: React.RefObject<HTMLDivElement>,
  barRef: React.RefObject<HTMLDivElement>,
  trackRef: React.RefObject<HTMLDivElement>,
  scrollbarRef: React.RefObject<HTMLDivElement>,
  deps: unknown[],
) {
  useEffect(() => {
    const container = containerRef.current;
    const bar = barRef.current;
    const track = trackRef.current;
    const scrollbarWrapper = scrollbarRef.current;
    if (!container || !bar || !track || !scrollbarWrapper) return;

    let maxScroll = 0;
    let rafId = 0;

    const checkScroll = () => {
      maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
      if (maxScroll <= 0) {
        scrollbarWrapper.classList.add("hidden");
        container.classList.remove(_DRAGGABLE_CLASS);
      } else {
        scrollbarWrapper.classList.remove("hidden");
        container.classList.add(_DRAGGABLE_CLASS);
      }
      return maxScroll;
    };

    const syncBar = () => {
      const maxScroll = checkScroll();
      if (maxScroll <= 0) return;
      const scrollRatio = container.scrollLeft / maxScroll;
      const maxMove = track.clientWidth - bar.clientWidth;
      bar.style.left = `${scrollRatio * maxMove}px`;
    };

    const scheduleSyncBar = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        syncBar();
      });
    };

    const handleWheel = (e: WheelEvent) => {
      const maxScroll = checkScroll();
      if (maxScroll <= 0) return; // 가로 스크롤 불가(테이블이 폭에 맞음) → 페이지 스크롤 허용
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // 가로 우세(트랙패드) → 네이티브 가로 처리
      // AISASSIT-31: 세로 휠을 가로 스크롤로 변환하되, 가로 양끝에서도 챗봇으로
      // 전파시키지 않고 가둔다(containment). 양끝에선 scrollLeft 가 클램프되어 no-op.
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    const onResize = () => {
      checkScroll();
      scheduleSyncBar();
    };

    /* ─── 마우스 드래그로 끌어서 스크롤 (grab-to-pan) ───
       네이티브 overflow 가로 스크롤은 마우스 드래그를 지원하지 않으므로 직접 구현.
       - 마우스 포인터만 처리(터치는 네이티브 가로 스크롤 유지)
       - 임계값 초과 이동 시에만 드래그로 간주 → SKU/PDP/장바구니 클릭과 공존
       - 드래그가 발생하면 이어지는 click 을 1회 억제 */
    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId: number | null = null;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      if (checkScroll() <= 0) return;
      isDown = true;
      dragged = false;
      startX = e.clientX;
      startScrollLeft = container.scrollLeft;
      activePointerId = e.pointerId;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!dragged) {
        if (Math.abs(dx) < _DRAG_THRESHOLD_PX) return;
        dragged = true;
        container.classList.add(_GRABBING_CLASS);
        if (activePointerId != null) {
          try {
            container.setPointerCapture(activePointerId);
          } catch {
            /* 캡처 미지원 환경은 무시 */
          }
        }
      }
      e.preventDefault();
      container.scrollLeft = startScrollLeft - dx;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      container.classList.remove(_GRABBING_CLASS);
      if (activePointerId != null) {
        try {
          container.releasePointerCapture(activePointerId);
        } catch {
          /* 이미 해제된 경우 무시 */
        }
      }
      activePointerId = null;
    };

    const onClickCapture = (e: MouseEvent) => {
      if (dragged) {
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }
    };

    /* ─── 스크롤바 thumb(bar) 직접 드래그 → 스크롤 (마우스·터치 공통) ───
       바를 잡고 끌면 이동량(dx)을 trackable 폭 대비 비율로 scrollLeft 에 매핑한다.
       바는 컨테이너 밖 요소라 grab-to-pan(container) 과 충돌하지 않는다. */
    let barDown = false;
    let barStartX = 0;
    let barStartScrollLeft = 0;
    let barPointerId: number | null = null;

    const onBarPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (checkScroll() <= 0) return;
      barDown = true;
      barStartX = e.clientX;
      barStartScrollLeft = container.scrollLeft;
      barPointerId = e.pointerId;
      try {
        bar.setPointerCapture(e.pointerId);
      } catch {
        /* 캡처 미지원 무시 */
      }
      container.classList.add(_GRABBING_CLASS);
      bar.style.transition = "none"; // 드래그 중 left transition lag 제거
      e.preventDefault();
      e.stopPropagation();
    };

    const onBarPointerMove = (e: PointerEvent) => {
      if (!barDown) return;
      const maxMove = track.clientWidth - bar.clientWidth;
      if (maxMove <= 0) return;
      const dx = e.clientX - barStartX;
      container.scrollLeft = barStartScrollLeft + (dx / maxMove) * maxScroll;
      e.preventDefault();
    };

    const endBarDrag = () => {
      if (!barDown) return;
      barDown = false;
      if (barPointerId != null) {
        try {
          bar.releasePointerCapture(barPointerId);
        } catch {
          /* 이미 해제됨 무시 */
        }
      }
      barPointerId = null;
      container.classList.remove(_GRABBING_CLASS);
      bar.style.transition = ""; // transition 복원
    };

    container.addEventListener("scroll", scheduleSyncBar);
    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", endDrag);
    container.addEventListener("pointercancel", endDrag);
    container.addEventListener("click", onClickCapture, true);
    bar.addEventListener("pointerdown", onBarPointerDown);
    bar.addEventListener("pointermove", onBarPointerMove);
    bar.addEventListener("pointerup", endBarDrag);
    bar.addEventListener("pointercancel", endBarDrag);
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(container);

    onResize();

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
      container.removeEventListener("scroll", scheduleSyncBar);
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", endDrag);
      container.removeEventListener("pointercancel", endDrag);
      container.removeEventListener("click", onClickCapture, true);
      bar.removeEventListener("pointerdown", onBarPointerDown);
      bar.removeEventListener("pointermove", onBarPointerMove);
      bar.removeEventListener("pointerup", endBarDrag);
      bar.removeEventListener("pointercancel", endBarDrag);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 행·열 변경 시 스크롤 재계산
  }, deps);
}
